import { Link } from 'react-router-dom';
import { Card, Button } from '@/components/ui';
import { Activity, ArrowRight, TrendingUp } from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { ChartDataPoint } from './types';

interface TrendChartProps {
  data: ChartDataPoint[];
}

export function TrendChart({ data }: TrendChartProps) {
  const hasData = data.some(d => d.entries > 0 || d.exits > 0);
  const totalEntries = data.reduce((sum, d) => sum + d.entries, 0);
  const totalExits = data.reduce((sum, d) => sum + d.exits, 0);

  return (
    <Card padding="md" className="h-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-900/50 rounded-lg">
            <Activity className="text-indigo-600 dark:text-indigo-400" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">
              Tendance des mouvements
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">7 derniers jours</p>
          </div>
        </div>
        <Link to="/analytics">
          <Button variant="ghost" size="sm" className="text-xs">
            Analytics
            <ArrowRight size={14} className="ml-1" />
          </Button>
        </Link>
      </div>

      {!hasData ? (
        <div className="h-[160px] sm:h-[200px] flex flex-col items-center justify-center text-center">
          <TrendingUp className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Aucun mouvement cette semaine</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Les données apparaîtront ici</p>
        </div>
      ) : (
        <>
          {/* Summary badges */}
          <div className="flex gap-4 mb-3">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 dark:bg-green-900/30 rounded-full">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <span className="text-xs font-medium text-green-700 dark:text-green-300">
                +{totalEntries} entrées
              </span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 dark:bg-red-900/30 rounded-full">
              <div className="w-2 h-2 rounded-full bg-red-500"></div>
              <span className="text-xs font-medium text-red-700 dark:text-red-300">
                -{totalExits} sorties
              </span>
            </div>
          </div>

          <div className="h-[130px] sm:h-[160px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorEntries" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorExits" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const chartData = payload[0]?.payload as ChartDataPoint;
                      return (
                        <div className="bg-white dark:bg-gray-800 p-2 rounded-lg shadow-lg border dark:border-gray-700 text-xs">
                          <p className="font-medium text-gray-900 dark:text-white mb-1">
                            {chartData.fullDate}
                          </p>
                          <p className="text-green-600">Entrées: {chartData.entries}</p>
                          <p className="text-red-600">Sorties: {chartData.exits}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="entries"
                  stroke="#10b981"
                  strokeWidth={2}
                  fill="url(#colorEntries)"
                  name="Entrées"
                />
                <Area
                  type="monotone"
                  dataKey="exits"
                  stroke="#ef4444"
                  strokeWidth={2}
                  fill="url(#colorExits)"
                  name="Sorties"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </Card>
  );
}
